const AUTHORITY = 'https://login.microsoftonline.com/common/oauth2/v2.0';
const SCOPE = 'openid profile email';
const FAILED = 'Microsoft sign-in failed';
const PENDING_KEY = 'microsoft-signin-pending';
const PENDING_TTL_MS = 10 * 60 * 1000;

export const isEmbeddedWebView = userAgent => /median|gonative/i.test(userAgent || '');

const microsoftRedirectUri = () => `${window.location.origin}/signin`;

export const isMicrosoftSignInPopup = () =>
  !!window.opener && /[#&]state=/.test(window.location.hash) && /[#&](code|error)=/.test(window.location.hash);

const base64Url = bytes =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const randomString = () => base64Url(crypto.getRandomValues(new Uint8Array(32)));

const pkceChallenge = async verifier =>
  base64Url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));

const tokenNonce = (idToken) => {
  try {
    const payload = idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload)).nonce;
  } catch (e) {
    return null;
  }
};

const cancelled = () => Object.assign(new Error('Microsoft sign-in was cancelled'), { cancelled: true });

export const parseFragment = hash => new URLSearchParams(String(hash || '').replace(/^#/, ''));

export const trimMicrosoftMessage = (description) => {
  const text = String(description || '');
  const labels = ['Trace ID:', 'Correlation ID:', 'Timestamp:'].map(l => text.indexOf(l)).filter(n => n >= 0);
  const end = labels.length ? Math.min(...labels) : text.length;
  return (
    text
      .slice(0, end)
      .replace(/[<>&"']/g, '')
      .trim() || FAILED
  );
};

const logCorrelationId = (description) => {
  const id = /Correlation ID:\s*(\S+)/.exec(description || '');
  if (id) console.warn(`Microsoft sign-in correlation id: ${id[1]}`);
};

export const validateReturn = (params, stored) => {
  const error = params.get('error');
  if (error === 'access_denied') throw cancelled();
  if (error) {
    if (!stored || params.get('state') !== stored.state) throw new Error(FAILED);
    const description = params.get('error_description');
    logCorrelationId(description);
    throw new Error(trimMicrosoftMessage(description));
  }
  const code = params.get('code');
  if (!stored || !stored.verifier || params.get('state') !== stored.state || !code) throw new Error(FAILED);
  return code;
};

const exchangeCode = async ({ clientId, verifier, nonce }, code) => {
  const res = await fetch(`${AUTHORITY}/token`, {
    method: 'POST',
    body: new URLSearchParams({
      client_id: clientId,
      grant_type: 'authorization_code',
      code,
      redirect_uri: microsoftRedirectUri(),
      code_verifier: verifier,
      scope: SCOPE
    })
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    logCorrelationId(body.error_description);
    throw new Error(trimMicrosoftMessage(body.error_description));
  }
  if (!body.id_token || tokenNonce(body.id_token) !== nonce) throw new Error(FAILED);
  return body.id_token;
};

const buildAuthorize = async (clientId) => {
  const stored = {
    verifier: randomString(),
    state: randomString(),
    nonce: randomString(),
    clientId
  };
  const query = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    redirect_uri: microsoftRedirectUri(),
    response_mode: 'fragment',
    scope: SCOPE,
    state: stored.state,
    nonce: stored.nonce,
    code_challenge: await pkceChallenge(stored.verifier),
    code_challenge_method: 'S256',
    prompt: 'select_account'
  });
  return { stored, url: `${AUTHORITY}/authorize?${query}` };
};

const waitForRedirect = popup =>
  new Promise((resolve, reject) => {
    const timer = setInterval(() => {
      if (popup.closed) {
        clearInterval(timer);
        reject(cancelled());
        return;
      }
      let href;
      try {
        ({ href } = popup.location);
      } catch (e) {
        return;
      }
      if (!href.startsWith(microsoftRedirectUri())) return;
      clearInterval(timer);
      const params = parseFragment(popup.location.hash);
      popup.close();
      resolve(params);
    }, 250);
  });

const isExpired = (stored, now) => !(now - stored.createdAt <= PENDING_TTL_MS);

const readPending = () => {
  let stored = null;
  try {
    stored = JSON.parse(window.sessionStorage.getItem(PENDING_KEY));
  } catch (e) {
    return null;
  }
  if (stored && isExpired(stored, Date.now())) {
    window.sessionStorage.removeItem(PENDING_KEY);
    return null;
  }
  return stored;
};

export const pendingReturnAction = (stored, currentFullPath, hash, now) => {
  const params = parseFragment(hash);
  if (!stored || isExpired(stored, now) || !(params.has('code') || params.has('error'))) return 'none';
  if (currentFullPath.split('#')[0] !== stored.returnPath) return { replaceTo: stored.returnPath + hash };
  return 'complete';
};

export const readPendingReturnAction = currentFullPath =>
  pendingReturnAction(readPending(), currentFullPath, window.location.hash, Date.now());

export async function signInWithMicrosoft(clientId) {
  window.sessionStorage.removeItem(PENDING_KEY);
  const popup = window.open('', 'mpower-microsoft-signin', 'width=500,height=640');
  let auth;
  try {
    auth = await buildAuthorize(clientId);
  } catch (e) {
    if (popup) popup.close();
    throw e;
  }
  const { stored, url } = auth;
  if (!popup) {
    const returnPath = window.location.pathname + window.location.search;
    window.sessionStorage.setItem(PENDING_KEY, JSON.stringify({ ...stored, returnPath, createdAt: Date.now() }));
    window.location.assign(url);
    return new Promise(() => {});
  }
  popup.location.href = url;
  const code = validateReturn(await waitForRedirect(popup), stored);
  return exchangeCode(stored, code);
}

export async function completeMicrosoftRedirect() {
  const stored = readPending();
  const params = parseFragment(window.location.hash);
  if (!stored || !(params.has('code') || params.has('error'))) return null;
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  window.sessionStorage.removeItem(PENDING_KEY);
  return exchangeCode(stored, validateReturn(params, stored));
}
